/**
 * In-editor help: the shortcut table plus the chrome controls that have no
 * key of their own.
 *
 * The shortcut list is the same array `buildKeymap` reads. A hand-maintained
 * help page that drifted from the keymap would be worse than no help at all.
 */

import { shortcutFor, shortcuts } from '@openleaf-editor/core'
import { ensureDialogStyles } from './dialog.js'
import { t, withLocale } from './i18n.js'
import { ensureStyles } from './styles.js'

const HELP_CHROME: Array<[string, string]> = [
  // Tab is not a keymap binding. Listing it here is how Help tells the
  // truth: it leaves the editor, including from a code block, and Alt+F10
  // is the toolbar shortcut. Binding Tab to indent would trap a keyboard
  // user in the contenteditable (WCAG 2.1.2); see keymap.ts.
  ['Tab', 'Leave the editor (including from a code block)'],
  ['Alt+F10', 'Move focus to the formatting toolbar'],
  ['Escape', 'Return focus to the document'],
  ['F1', 'Open this help dialog'],
]

const CODE_BLOCK_HINT =
  'In a code block, indent by typing spaces. Tab is never captured, including there.'

/**
 * Unique per dialog.
 *
 * `ol-help-title` was a constant, so a second editor's help dialog carried the
 * same id and `aria-labelledby` resolved to whichever was first in the
 * document -- naming this dialog after somebody else's.
 *
 * `host` is the editor to mount inside, so the dialog inherits its skin, and it
 * is where the locale comes from: the editor's own `lang`, not the
 * document-wide one. One argument answers both because both are properties of
 * the same editor. It stays optional so the existing `promptHelp(doc)` shape
 * keeps working.
 */
let helpCounter = 0

export function promptHelp(doc: Document, host?: HTMLElement): void {
  withLocale(host?.getAttribute('lang') ?? null, () => buildHelp(doc, host))
}

function buildHelp(doc: Document, host?: HTMLElement): void {
  ensureStyles(doc)
  ensureDialogStyles(doc)
  const previouslyFocused = doc.activeElement as HTMLElement | null
  const dialog = doc.createElement('dialog')
  dialog.className = 'ol-dialog ol-help'
  const headingId = `ol-help-title-${(helpCounter += 1)}`
  dialog.setAttribute('aria-labelledby', headingId)

  const form = doc.createElement('form')
  form.method = 'dialog'

  const heading = doc.createElement('h2')
  heading.id = headingId
  heading.textContent = t('Keyboard shortcuts')
  form.appendChild(heading)

  const table = doc.createElement('table')
  table.className = 'ol-help-table'
  const body = doc.createElement('tbody')
  for (const shortcut of shortcuts) {
    const row = doc.createElement('tr')
    const keys = doc.createElement('th')
    keys.scope = 'row'
    keys.textContent = shortcutFor(shortcut.label) ?? shortcut.keys
    const label = doc.createElement('td')
    label.textContent = t(shortcut.label)
    row.appendChild(keys)
    row.appendChild(label)
    body.appendChild(row)
  }
  for (const [keys, label] of HELP_CHROME) {
    const row = doc.createElement('tr')
    const keyCell = doc.createElement('th')
    keyCell.scope = 'row'
    keyCell.textContent = keys
    const labelCell = doc.createElement('td')
    labelCell.textContent = t(label)
    row.appendChild(keyCell)
    row.appendChild(labelCell)
    body.appendChild(row)
  }
  table.appendChild(body)
  form.appendChild(table)

  const hint = doc.createElement('p')
  hint.className = 'ol-hint'
  // Do not mention Mod-] here. indent asks enclosingList first, so a caret
  // in a code block inside a list item still nests that item — claiming the
  // chord is a no-op would be a lie, and a subordinate clause about list
  // nesting does not belong in F1. Indent is already in the shortcut table.
  hint.textContent = t(CODE_BLOCK_HINT)
  form.appendChild(hint)

  const actions = doc.createElement('div')
  actions.className = 'ol-actions'
  const close = doc.createElement('button')
  close.type = 'submit'
  close.value = 'ok'
  close.textContent = t('Close')
  actions.appendChild(close)
  form.appendChild(actions)
  dialog.appendChild(form)
  // Inside the editor, so the skin reaches it. `showModal` promotes to the top
  // layer regardless of where the element sits, so nesting costs nothing.
  ;(host && host.ownerDocument === doc ? host : doc.body).appendChild(dialog)
  // The close button is a submit button in a `method="dialog"` form, so closing
  // fires `submit`. Mounted inside the editor that now bubbles into the host
  // page's own form; stop it, or dismissing help looks like saving the document.
  form.addEventListener('submit', (event) => {
    event.stopPropagation()
  })
  dialog.addEventListener('close', () => {
    dialog.remove()
    previouslyFocused?.focus()
  })
  dialog.showModal()
}
